const YOUTUBE_VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
]);

const YOUTUBE_SHORT_HOSTS = new Set(["youtu.be", "www.youtu.be"]);

export interface YouTubeEmbedOptions {
  privacyEnhanced?: boolean;
  autoplay?: boolean;
  controls?: boolean;
  loop?: boolean;
  muted?: boolean;
  startSeconds?: number;
}

function isYouTubeVideoId(value: string): boolean {
  return YOUTUBE_VIDEO_ID_PATTERN.test(value);
}

function parseYouTubeUrl(value: string): URL | null {
  const candidate = /^[a-z][a-z\d+.-]*:\/\//i.test(value)
    ? value
    : `https://${value}`;

  try {
    const url = new URL(candidate);
    return url.protocol === "http:" || url.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

function videoIdFromPath(pathname: string, prefix: string): string | null {
  if (!pathname.startsWith(prefix)) return null;
  const videoId = pathname.slice(prefix.length).split("/")[0] ?? "";
  return isYouTubeVideoId(videoId) ? videoId : null;
}

export function normalizeYouTubeVideoId(source: unknown): string | null {
  if (typeof source !== "string") return null;

  const value = source.trim();
  if (!value) return null;
  if (isYouTubeVideoId(value)) return value;

  const url = parseYouTubeUrl(value);
  if (!url || url.username || url.password) return null;

  const hostname = url.hostname.toLowerCase().replace(/\.$/, "");

  if (YOUTUBE_SHORT_HOSTS.has(hostname)) {
    const videoId = url.pathname.split("/").filter(Boolean)[0] ?? "";
    return isYouTubeVideoId(videoId) ? videoId : null;
  }

  if (!YOUTUBE_HOSTS.has(hostname)) return null;

  if (url.pathname === "/watch") {
    const videoId = url.searchParams.get("v") ?? "";
    return isYouTubeVideoId(videoId) ? videoId : null;
  }

  return (
    videoIdFromPath(url.pathname, "/embed/") ??
    videoIdFromPath(url.pathname, "/shorts/")
  );
}

function normalizeStartSeconds(value: unknown): number {
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return 0;
  return Math.min(86_400, Math.max(0, Math.floor(seconds)));
}

export function buildYouTubeEmbedUrl(
  source: unknown,
  options: YouTubeEmbedOptions = {},
): string | null {
  const videoId = normalizeYouTubeVideoId(source);
  if (!videoId) return null;

  const hostname = options.privacyEnhanced === false
    ? "www.youtube.com"
    : "www.youtube-nocookie.com";
  const url = new URL(`https://${hostname}/embed/${videoId}`);

  url.searchParams.set("playsinline", "1");
  url.searchParams.set("rel", "0");
  url.searchParams.set("controls", options.controls === false ? "0" : "1");
  url.searchParams.set("cc_load_policy", "0");

  if (options.autoplay === true) {
    url.searchParams.set("autoplay", "1");
    url.searchParams.set("mute", "1");
  } else if (options.muted === true) {
    url.searchParams.set("mute", "1");
  }

  if (options.loop === true) {
    url.searchParams.set("loop", "1");
    url.searchParams.set("playlist", videoId);
  }

  const startSeconds = normalizeStartSeconds(options.startSeconds);
  if (startSeconds > 0) {
    url.searchParams.set("start", String(startSeconds));
  }

  return url.toString();
}

export function buildYouTubeThumbnailUrl(source: unknown): string | null {
  const videoId = normalizeYouTubeVideoId(source);
  return videoId
    ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
    : null;
}
